// US-only timezone list for auction pickers.
const US_TIMEZONES: readonly string[] = [
  // Eastern
  'America/New_York', 'America/Detroit', 'America/Kentucky/Louisville',
  'America/Kentucky/Monticello', 'America/Indiana/Indianapolis',
  'America/Indiana/Vincennes', 'America/Indiana/Winamac',
  'America/Indiana/Marengo', 'America/Indiana/Petersburg',
  'America/Indiana/Vevay', 'America/Indiana/Tell_City',
  // Central
  'America/Chicago', 'America/Indiana/Knox', 'America/Menominee',
  'America/North_Dakota/Center', 'America/North_Dakota/New_Salem',
  // Mountain
  'America/Denver', 'America/Boise', 'America/Phoenix',
  // Pacific
  'America/Los_Angeles',
  // Alaska & Hawaii
  'America/Anchorage', 'America/Juneau', 'America/Sitka',
  'America/Metlakatla', 'America/Nome', 'America/Adak', 'Pacific/Honolulu',
  // Territories
  'Pacific/Pago_Pago', 'Pacific/Guam', 'Pacific/Saipan',
  'America/Puerto_Rico', 'America/St_Thomas',
];

export function getUSTimezoneOptions(): string[] {
  try {
    const supported = new Set(Intl.supportedValuesOf('timeZone'));
    return US_TIMEZONES.filter(tz => supported.has(tz)).sort();
  } catch {
    return [...US_TIMEZONES].sort();
  }
}

// IANA-timezone helpers for the admin UI. PrimeVue's DatePicker has no
// timezone prop — it always reads/writes Date objects in the browser's
// local timezone. These helpers shift a UTC ISO string into a Date whose
// browser-local components match the wall-clock in `auctionTz`, and
// reverse the shift on write. Mirrors the backend's
// `domain/scheduler/timezone.ts` `localToUtc` / `tzOffsetMinutes` pair.

export function tzOffsetMinutes(utcMs: number, ianaTz: string): number {
  const dtf = new Intl.DateTimeFormat('en-US', {
    timeZone: ianaTz,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
  const parts = dtf.formatToParts(new Date(utcMs));
  const lookup: Record<string, string> = {};
  for (const p of parts) lookup[p.type] = p.value;
  const hour = Number(lookup.hour) === 24 ? 0 : Number(lookup.hour);
  const asUtc = Date.UTC(
    Number(lookup.year),
    Number(lookup.month) - 1,
    Number(lookup.day),
    hour,
    Number(lookup.minute),
    Number(lookup.second),
  );
  return Math.round((asUtc - utcMs) / 60_000);
}

// Given a real UTC instant and an IANA zone, return a Date whose
// browser-local components (getHours/getDate/etc.) match the wall-clock
// in `auctionTz`. Feed this into PrimeVue's DatePicker to make it
// display the auction's local time instead of the admin's browser time.
export function utcIsoToAuctionTzDate(iso: string | null | undefined, auctionTz: string): Date | null {
  if (!iso) return null;
  const real = new Date(iso);
  if (Number.isNaN(real.getTime())) return null;
  const dtf = new Intl.DateTimeFormat('en-US', {
    timeZone: auctionTz,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
  const parts = dtf.formatToParts(real);
  const lookup: Record<string, string> = {};
  for (const p of parts) lookup[p.type] = p.value;
  const hour = Number(lookup.hour) === 24 ? 0 : Number(lookup.hour);
  return new Date(
    Number(lookup.year),
    Number(lookup.month) - 1,
    Number(lookup.day),
    hour,
    Number(lookup.minute),
    Number(lookup.second),
  );
}

// Inverse: given a Date whose browser-local components are the desired
// wall-clock in `auctionTz`, return the ISO string of the real UTC
// instant. Two-pass DST-safe (matches backend's `localToUtc`).
export function auctionTzDateToUtcIso(d: Date | null, auctionTz: string): string {
  if (!d) return '';
  const baseUtc = Date.UTC(
    d.getFullYear(),
    d.getMonth(),
    d.getDate(),
    d.getHours(),
    d.getMinutes(),
    d.getSeconds(),
  );
  let utc = baseUtc;
  for (let i = 0; i < 2; i++) {
    const offsetMin = tzOffsetMinutes(utc, auctionTz);
    utc = baseUtc - offsetMin * 60_000;
  }
  return new Date(utc).toISOString();
}

export function formatTimestamp(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const now = new Date();
  const sameYear = d.getFullYear() === now.getFullYear();
  const month = d.toLocaleDateString('en-US', { month: 'short' });
  const day = d.getDate();
  const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  if (sameYear) return `${month} ${day}, ${time}`;
  return `${month} ${day}, ${d.getFullYear()}, ${time}`;
}
